// ============================================================
// TRANSLATION HOOK - Easy translation access throughout app
// ============================================================

import { useState, useEffect, createContext, useContext } from 'react';
import translations from './translations';

// Create Translation Context
const TranslationContext = createContext();

// Translation Provider Component
export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('selectedLanguage', language);
  }, [language]);

  const changeLanguage = (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('selectedLanguage', newLang);
  };

  const value = {
    language,
    changeLanguage,
    t: (key) => getTranslation(key, language)
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// Hook to use translations
export const useTranslate = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslate must be used within TranslationProvider');
  }
  return context;
};

// Helper function to get nested translation
const getTranslation = (key, language = 'en') => {
  const keys = key.split('.');
  let value = translations[language];
  
  for (const k of keys) {
    if (value && typeof value === 'object') {
      value = value[k];
    } else {
      // Fallback to English if translation not found
      value = translations.en;
      for (const k2 of keys) {
        if (value && typeof value === 'object') {
          value = value[k2];
        } else {
          return key; // Return key if not found
        }
      }
      break;
    }
  }
  
  return value || key;
};

// Standalone translation function (for use outside components)
export const translate = (key, language) => {
  const lang = language || localStorage.getItem('selectedLanguage') || 'en';
  return getTranslation(key, lang);
};

export default useTranslate;
